import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Search, ArrowRight, UserPlus, LogIn, Users, Shield, Vote, Building2, GraduationCap, Network, Lightbulb } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';

const ProfessionalsPage = () => {
  // Four main categories
  const categories = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'प्रशासन',
      titleEn: 'Administration',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      icon: <Vote className="w-6 h-6" />,
      title: 'राजनीति',
      titleEn: 'Politics',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      icon: <Building2 className="w-6 h-6" />,
      title: 'उद्यम',
      titleEn: 'Enterprise',
      color: 'bg-green-50 text-green-600'
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: 'पेशा',
      titleEn: 'Profession',
      color: 'bg-orange-50 text-orange-600'
    }
  ];

  const features = [
    {
      icon: <Search className="w-6 h-6" />,
      title: 'Find Professionals',
      description: 'Search and connect with doctors, lawyers, CAs, engineers and other professionals from our community'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Networking',
      description: 'Build professional connections within the Brahmkshatriya community'
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: 'Business Opportunities',
      description: 'Discover business and collaboration opportunities with community members'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="gradient-orange py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-6">
            <a href="/" className="hover:text-white">Home</a>
            <span>›</span>
            <span>Professionals Data</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Professionals Data
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              व्यावसायिक डेटा - Professional Directory & Network
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden mb-12"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary/10 to-transparent p-6 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Network className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">प्रोफेशनल्स डेटा संग्रह</h2>
            </div>
            <p className="text-gray-600">
              हमारे समाज बंधु विभिन्न क्षेत्रों में कार्यरत हैं। इन क्षेत्रों को मुख्यतः चार श्रेणियों में रखा में जा सकता है:
            </p>
          </div>

          <div className="p-6 space-y-8">
            {/* Four Categories */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {categories.map((category, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className={`${category.color} rounded-xl p-4 text-center`}
                >
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                    {category.icon}
                  </div>
                  <h3 className="font-bold text-lg">{category.title}</h3>
                  <p className="text-sm opacity-80">{category.titleEn}</p>
                </motion.div>
              ))}
            </div>

            {/* Description */}
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 leading-relaxed">
                इन श्रेणियों में उपश्रेणियां खंड (Segment) के अनुसार अनेक हो सकती हैं। स्थितियां (Status) भी अनेक हो सकती हैं। किसी भी सेगमेंट, किसी भी स्थिति में व किसी भी स्थान पर आप व हम हैं, <strong className="text-primary">हमारा प्रोफेशनल डेटा एकत्र करना आवश्यक प्रतीत होता है।</strong>
              </p>
              <p className="text-gray-600 leading-relaxed">
                डेटा कलेक्शन के बाद हम आपस में मिलें व यह जानने का प्रयास करें कि हम आपस में क्या सहयोग कर सकते हैं। समाज के लिये क्या कर सकते हैं। हम हमारे युवाओं को क्या दिशा दे सकते हैं। उन्हें कैसे आगे बढा सकते हैं।
              </p>
            </div>

            {/* Vision Box */}
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-gray-700 leading-relaxed">
                    इस विचारधारा व उद्देश्य से समाज के विभिन्न क्षेत्रों में स्थापित प्रोफेशनल्स का डाटा एकत्र करने का यह प्रयास है। आपस में नेटवर्क कैसे बनें? पर विचार कर कुछ गतिविधियां जैसे <strong>सेमीनार, कॉन्फ्रेंस, वेबीनार</strong> आदि आयोजित करने की योजना बनेगी। यह नेटवर्क हमें व हमारे युवाओं को आगे ले जाएगा।
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-primary/5 to-transparent rounded-xl p-6 text-center">
              <p className="text-gray-700 font-medium mb-4 text-lg">
                अतः प्रोफेशनल्स व्यावसायिक पंजीकरण के लिये आमंत्रित हैं।
              </p>
              <Link to="/professional/register">
                <Button className="bg-primary hover:bg-primary/90 text-white px-8 py-3 text-lg">
                  <UserPlus className="w-5 h-5 mr-2" />
                  Register as Professional
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Main Action Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {/* Register Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl border border-gray-100 shadow-card p-8 hover:shadow-lg transition-shadow"
          >
            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
              <UserPlus className="w-7 h-7 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Register as Professional</h2>
            <p className="text-gray-600 mb-6">
              Join our professional network. Register your details to be visible to community members seeking your services.
            </p>
            <ul className="text-sm text-gray-500 mb-6 space-y-2">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                Get listed in our directory
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                Connect with community members
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                Grow your professional network
              </li>
            </ul>
            <Link to="/professional/register">
              <Button className="bg-primary hover:bg-primary-600 text-white w-full">
                Register Now <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </motion.div>

          {/* Login Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl border border-gray-100 shadow-card p-8 hover:shadow-lg transition-shadow"
          >
            <div className="w-14 h-14 bg-green-50 rounded-xl flex items-center justify-center mb-6">
              <LogIn className="w-7 h-7 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Professional Login</h2>
            <p className="text-gray-600 mb-6">
              Already registered? Login to access the full directory, update your profile, and connect with other professionals.
            </p>
            <ul className="text-sm text-gray-500 mb-6 space-y-2">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                Browse full professional directory
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                Update your profile & photo
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                View contact details
              </li>
            </ul>
            <Link to="/professional/login">
              <Button variant="outline" className="border-green-200 text-green-600 hover:bg-green-50 w-full">
                Login <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </motion.div>

          {/* Directory Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl border border-gray-100 shadow-card p-8 hover:shadow-lg transition-shadow"
          >
            <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
              <Search className="w-7 h-7 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Browse Directory</h2>
            <p className="text-gray-600 mb-6">
              Search and find professionals from our community. Filter by profession, location, or specialization.
            </p>
            <ul className="text-sm text-gray-500 mb-6 space-y-2">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                Doctors, Lawyers, CAs, Engineers
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                Search by city or state
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                Filter by specialization
              </li>
            </ul>
            <Link to="/professional/directory">
              <Button variant="outline" className="border-blue-200 text-blue-600 hover:bg-blue-50 w-full">
                View Directory <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>

        {/* Features Section */}
        <div className="bg-gray-50 rounded-2xl p-8 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Why Join Our Professional Network?</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 text-primary shadow-sm">
                  {feature.icon}
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{feature.title}</h4>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Professional Categories */}
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Professional Categories</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'Doctor', 'Lawyer', 'CA', 'Engineer', 'Teacher', 'Professor',
              'Architect', 'Government Officer', 'Business Owner', 'IT Professional',
              'Banker', 'Company Secretary', 'Consultant', 'Journalist'
            ].map((category, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:border-primary hover:text-primary transition-colors"
              >
                {category}
              </span>
            ))}
          </div>
        </div>
      </div>
      </section>
    </div>
  );
};

export default ProfessionalsPage;
